import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { createProject, uploadProjectImage } from '../../services/projectService';
import { 
  Building2, 
  X, 
  Hash, 
  Tag, 
  MapPin, 
  Activity, 
  Calendar, 
  Image as ImageIcon, 
  Sparkles, 
  AlertCircle, 
  Loader2,
  Upload,
  Trash2,
  FileImage
} from 'lucide-react';

export const CATEGORIES = [
  'Commercial',
  'Residential',
  'Infrastructure',
  'Industrial',
  'Highway & Roadways',
  'Healthcare',
  'Educational',
  'Mixed-Use',
  'Energy & Utilities',
  'Other'
];

export const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active', color: '#10B981' },
  { value: 'Upcoming', label: 'Upcoming', color: '#F59E0B' },
  { value: 'On Hold', label: 'On Hold', color: '#EF4444' },
  { value: 'Completed', label: 'Completed', color: '#A855F7' }
];

export default function AddProjectModal({ isOpen, onClose, onProjectAdded }) {
  const { isDarkMode } = useTheme();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    displayName: '',
    category: 'Commercial',
    city: '',
    state: '',
    status: 'Active',
    lastSurveyDate: '',
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const [displayNameManuallyEdited, setDisplayNameManuallyEdited] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        code: '',
        displayName: '',
        category: 'Commercial',
        city: '',
        state: '',
        status: 'Active',
        lastSurveyDate: new Date().toISOString().split('T')[0],
      });
      setImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      setImageFile(null);
      setIsDragging(false);
      setUploadStatus('');
      setCodeManuallyEdited(false);
      setDisplayNameManuallyEdited(false);
      setErrors({});
      setServerError('');
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleImageSelect = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setServerError('Please select an image file (PNG, JPG, WEBP, SVG).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setServerError('Image size must be less than 10MB.');
      return;
    }
    setServerError('');
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageSelect(e.dataTransfer.files[0]);
    }
  };

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen && !submitting) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  // Auto-generate project code from name (e.g., "Skyline Park" -> "PRJ-SKY-01")
  const generateCodeFromName = (name) => {
    if (!name.trim()) return '';
    const clean = name.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    const parts = clean.split(/\s+/);
    let initials = '';
    if (parts.length >= 2) {
      initials = parts.slice(0, 3).map(p => p.slice(0, 3).toUpperCase()).join('');
    } else {
      initials = clean.slice(0, 4).toUpperCase();
    }
    const randNum = Math.floor(10 + Math.random() * 90);
    return `PRJ-${initials || 'GEN'}-${randNum}`;
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    const updatedData = { ...formData, name: newName };

    if (!displayNameManuallyEdited) {
      updatedData.displayName = newName;
    }

    if (!codeManuallyEdited && newName.trim().length >= 3) {
      updatedData.code = generateCodeFromName(newName);
    }

    setFormData(updatedData);

    if (errors.name) {
      setErrors(prev => ({ ...prev, name: '' }));
    }
  };

  const handleAutoGenerateCode = () => {
    const generated = generateCodeFromName(formData.name || 'NEW');
    setFormData(prev => ({ ...prev, code: generated }));
    setCodeManuallyEdited(true);
    if (errors.code) {
      setErrors(prev => ({ ...prev, code: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // UI-side validation only as requested
    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }
    if (!formData.code.trim()) {
      newErrors.code = 'Project code is required';
    } else if (formData.code.length > 50) {
      newErrors.code = 'Code cannot exceed 50 characters';
    }
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      let finalImageUrl = null;
      if (imageFile) {
        setUploadStatus('Uploading image to media server...');
        finalImageUrl = await uploadProjectImage(imageFile);
      }

      setUploadStatus('Creating project...');
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        displayName: formData.displayName.trim() || formData.name.trim(),
        category: formData.category || null,
        city: formData.city.trim() || null,
        state: formData.state.trim() || null,
        status: formData.status || 'Active',
        imageUrl: finalImageUrl || null,
        lastSurveyDate: formData.lastSurveyDate ? `${formData.lastSurveyDate}T00:00:00` : null,
      };

      const created = await createProject(payload);
      if (onProjectAdded) {
        onProjectAdded(created);
      }
      onClose();
    } catch (err) {
      console.error('Failed to create project:', err);
      setServerError(err.message || 'Failed to create project. Please check fields and try again.');
    } finally {
      setSubmitting(false);
      setUploadStatus('');
    }
  };

  const inputStyle = (hasError) => ({
    width: '100%',
    padding: '9px 12px',
    borderRadius: '8px',
    border: `1px solid ${hasError ? '#EF4444' : (isDarkMode ? '#252D3D' : '#D1D5DB')}`,
    backgroundColor: isDarkMode ? '#161B26' : '#FFFFFF',
    color: isDarkMode ? '#F3F4F6' : '#111827',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  });

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 600,
    color: isDarkMode ? '#9CA3AF' : '#4B5563',
    marginBottom: '6px',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(3px)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: 'fadeIn 0.15s ease-out',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '680px',
          maxHeight: '90vh',
          backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
          border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
          borderRadius: '16px',
          boxShadow: isDarkMode ? '0 20px 40px rgba(0,0,0,0.6)' : '0 20px 40px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: isDarkMode ? '#141A28' : '#F9FAFB',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 130, 248, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0082F8',
              }}
            >
              <Building2 size={22} />
            </div>
            <div>
              <h3 style={{ fontSize: '17px', fontWeight: 700, margin: 0, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                Add New Project
              </h3>
              <p style={{ fontSize: '12px', margin: '2px 0 0 0', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                Fill in the details to add a construction project
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              background: 'none',
              border: 'none',
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              cursor: submitting ? 'not-allowed' : 'pointer',
              padding: '6px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body / Scrollable Form */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {serverError && (
            <div
              style={{
                padding: '12px 14px',
                borderRadius: '8px',
                backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : '#FEE2E2',
                border: '1px solid #EF4444',
                color: '#EF4444',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '18px',
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>{serverError}</span>
            </div>
          )}

          <form id="add-project-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Row 1: Project Name (Required UI) & Code (Required UI) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              <div>
                <label style={labelStyle}>
                  <Building2 size={13} color="#0082F8" />
                  Project Name <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Prestige Tech Park Phase 2"
                  value={formData.name}
                  onChange={handleNameChange}
                  style={inputStyle(!!errors.name)}
                />
                {errors.name && (
                  <span style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px', display: 'block' }}>
                    {errors.name}
                  </span>
                )}
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>
                    <Hash size={13} color="#0082F8" />
                    Project Code <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleAutoGenerateCode}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#0082F8',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: 0,
                    }}
                  >
                    <Sparkles size={11} /> Auto Code
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="e.g. PRJ-PTP-02"
                  value={formData.code}
                  onChange={(e) => {
                    setFormData({ ...formData, code: e.target.value.toUpperCase() });
                    setCodeManuallyEdited(true);
                    if (errors.code) setErrors(prev => ({ ...prev, code: '' }));
                  }}
                  style={inputStyle(!!errors.code)}
                />
                {errors.code && (
                  <span style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px', display: 'block' }}>
                    {errors.code}
                  </span>
                )}
              </div>
            </div>

            {/* Row 2: Display Name (Required UI) & Category */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              <div>
                <label style={labelStyle}>
                  Display Name <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Prestige Tech Park"
                  value={formData.displayName}
                  onChange={(e) => {
                    setFormData({ ...formData, displayName: e.target.value });
                    setDisplayNameManuallyEdited(true);
                    if (errors.displayName) setErrors(prev => ({ ...prev, displayName: '' }));
                  }}
                  style={inputStyle(!!errors.displayName)}
                />
                {errors.displayName && (
                  <span style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px', display: 'block' }}>
                    {errors.displayName}
                  </span>
                )}
              </div>

              <div>
                <label style={labelStyle}>
                  <Tag size={13} color="#0082F8" />
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  style={{ ...inputStyle(false), cursor: 'pointer' }}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3: City & State */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              <div>
                <label style={labelStyle}>
                  <MapPin size={13} color="#0082F8" />
                  City
                </label>
                <input
                  type="text"
                  placeholder="e.g. Bengaluru"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  style={inputStyle(false)}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  <MapPin size={13} color="#0082F8" />
                  State
                </label>
                <input
                  type="text"
                  placeholder="e.g. Karnataka"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  style={inputStyle(false)}
                />
              </div>
            </div>

            {/* Row 4: Status & Last Survey Date */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              <div>
                <label style={labelStyle}>
                  <Activity size={13} color="#0082F8" />
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={{ ...inputStyle(false), cursor: 'pointer' }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>
                  <Calendar size={13} color="#0082F8" />
                  Last Survey Date
                </label>
                <input
                  type="date"
                  value={formData.lastSurveyDate}
                  onChange={(e) => setFormData({ ...formData, lastSurveyDate: e.target.value })}
                  style={inputStyle(false)}
                />
              </div>
            </div>

            {/* Row 5: Project Banner Image File Upload */}
            <div>
              <label style={labelStyle}>
                <ImageIcon size={13} color="#0082F8" />
                Project Banner Image
              </label>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/jpg"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleImageSelect(e.target.files[0]);
                  }
                }}
              />

              {!imagePreview ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    border: `2px dashed ${isDragging ? '#0082F8' : (isDarkMode ? '#252D3D' : '#D1D5DB')}`,
                    borderRadius: '10px',
                    padding: '24px 16px',
                    textAlign: 'center',
                    backgroundColor: isDragging 
                      ? (isDarkMode ? 'rgba(0, 130, 248, 0.1)' : 'rgba(0, 130, 248, 0.05)')
                      : (isDarkMode ? '#161B26' : '#F9FAFB'),
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <div
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '50%',
                      backgroundColor: isDarkMode ? '#1E2433' : '#E0E7FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#0082F8',
                    }}
                  >
                    <Upload size={18} />
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                      Click to browse
                    </span>{' '}
                    <span style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                      or drag & drop project image
                    </span>
                  </div>
                  <span style={{ fontSize: '11px', color: isDarkMode ? '#6B7280' : '#9CA3AF' }}>
                    PNG, JPG, WEBP or SVG up to 10MB (stored in command center media)
                  </span>
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: `1px solid ${isDarkMode ? '#252D3D' : '#E5E7EB'}`,
                    backgroundColor: isDarkMode ? '#161B26' : '#F9FAFB',
                    gap: '14px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: '76px',
                        height: '54px',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        flexShrink: 0,
                        border: `1px solid ${isDarkMode ? '#252D3D' : '#D1D5DB'}`,
                        backgroundColor: '#000',
                      }}
                    >
                      <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#F3F4F6' : '#111827', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {imageFile?.name || 'Selected project image'}
                      </div>
                      <div style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FileImage size={12} />
                        {imageFile ? `${(imageFile.size / 1024).toFixed(1)} KB` : ''} • Ready to upload
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: `1px solid ${isDarkMode ? '#252D3D' : '#D1D5DB'}`,
                        backgroundColor: isDarkMode ? '#1E2433' : '#FFF',
                        color: isDarkMode ? '#E5E7EB' : '#374151',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      title="Remove image"
                      style={{
                        padding: '6px 8px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        color: '#EF4444',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Modal Footer Actions */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
            backgroundColor: isDarkMode ? '#141A28' : '#F9FAFB',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '9px 18px',
              borderRadius: '8px',
              border: `1px solid ${isDarkMode ? '#252D3D' : '#D1D5DB'}`,
              backgroundColor: isDarkMode ? '#161B26' : '#FFFFFF',
              color: isDarkMode ? '#E5E7EB' : '#374151',
              fontSize: '13px',
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>

          <button
            type="submit"
            form="add-project-form"
            disabled={submitting}
            style={{
              padding: '9px 22px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#0082F8',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(0, 130, 248, 0.3)',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {uploadStatus || 'Creating...'}
              </>
            ) : (
              'Create Project'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
